<template>
  <div>
    <div
      v-for="cert in documents"
      :key="cert.id"
      class="card p-3 shadow-sm mb-3 border-0 cert-card"
      style="border-radius: 12px; border-left: 4px solid #e0f0ec;"
    >
      <div class="card-body p-0">
        <div class="row align-items-center">
          <div class="col-12 col-md-8 d-flex flex-column">
            <div class="d-flex align-items-center gap-2 mb-1">
              <div class="fw-bold fs-5 text-dark">
                {{ productName }}
              </div>
              <div class="text-muted small">
                N° {{ subscription.reference }}
              </div>
            </div>

            <div class="d-flex flex-column gap-1 mb-3">
              <div
                v-if="subscription.productId === 16"
                class="text-muted small"
              >
                <font-awesome-icon
                  icon="map-marker-alt"
                  class="me-1"
                /> {{ address }}
              </div>
              <div
                v-if="cert.type === 'CERTIFICATE_VISA'"
                class="text-muted small"
              >
                <font-awesome-icon
                  icon="file-alt"
                  class="me-1"
                /> {{ cert.documentName }}
              </div>
              <div
                v-if="getActivityForCert(cert)"
                class="text-muted small"
              >
                {{ getActivityForCert(cert) }}
              </div>
            </div>

            <div class="d-flex flex-wrap gap-2 mt-auto align-items-center">
              <div
                class="badge badge-pills rounded-pill fs-6 text-blue-700"
                :class="['CERTIFICATE'].includes(cert.type) ? 'bg-green-300' : 'bg-gray-300'"
              >
                {{ getCertificateName(cert) }}
              </div>

              <div class="small">
                Du {{ formatDate(cert.effectiveDate) }} au {{ cert.endEffectiveDate ? formatDate(cert.endEffectiveDate) : '...' }}
              </div>
            </div>
          </div>

          <div class="col-12 col-md-4 mt-3 mt-md-0 d-flex flex-column justify-content-center align-items-md-end gap-2">
            <template v-if="cert.type === 'CERTIFICATE' && subscription.productId === 13">
              <v-loader
                :next="() => { return downloadCertificate (cert) }"
                title="Télécharger"
                class="btn-download-pill"
              />
              <v-loader
                :next="openEmployeeTravelModal"
                title="Attestation nominative"
                icon="plus-white"
                class="w-100"
              />
            </template>
            <template v-else>
              <v-loader
                :next="() => { return downloadCertificate (cert) }"
                title="Télécharger"
                class="btn-download-pill"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { get } from 'lodash-es';
import { mapGetters } from 'vuex';
import { ui } from '@/mixins';

import formatter from '@/utils/_Strings';

const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

export default {
  components: {
  },
  mixins: [ui],
  props: {
    subscription: {
      type: Object,
      required: true,
      default: () => {},
    },
    activeTab: {
      type: String,
      default: 'CURRENT',
    },
    certificateTypeFilter: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      certificates: [{ key: 'CERTIFICATE_ACTIVITY', value: 'Attestation Orias' }, { key: 'CERTIFICATE', value: 'Attestation principale' },
        { key: 'CERTIFICATE_ACI', value: 'Attestation agent commercial immobiler' }, { key: 'CERTIFICATE_RDD', value: 'Attestation responsbilité des dirigeants' },
        { key: 'CERTIFICATE_AI', value: 'Attestation agent immobilier' }, { key: 'CERTIFICATE_GARFI', value: 'Garantie financiere' }, { key: 'CERTIFICATE_VISA', value: 'Mission' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      products: ['rater/products'],
    }),
    address() {
      if (this.subscription.productId === 16) {
        return formatter.addressFormatter(this.subscription.risk.local);
      }
      return null;
    },
    mainActivity() {
      return get(this.subscription, 'risk.mainActivity.name', null);
    },
    productName() {
      return (this.products
        .find(({ id }) => id === this.subscription.productId)
        || {})
        .name;
    },
    activityCertificates() {
      const isCurrent = this.activeTab === 'CURRENT';
      return (this.subscription.documents || [])
        .filter(({ type, current }) => type === 'CERTIFICATE_ACTIVITY' && current === isCurrent);
    },
    documents() {
      const { subscription } = this;

      const mainCertificates = (subscription.documents || [])
        .filter(({ type }) => ['CERTIFICATE'].includes(type));

      const otherCertificates = (subscription.documents || [])
        .filter(({ type }) => ['CERTIFICATE_ACI', 'CERTIFICATE_AI', 'CERTIFICATE_ACTIVITY', 'CERTIFICATE_RDD', 'CERTIFICATE_GARFI', 'CERTIFICATE_VISA'].includes(type));

      let allDocs = mainCertificates.concat(otherCertificates);

      if (this.certificateTypeFilter) {
        allDocs = allDocs.filter(({ type }) => type === this.certificateTypeFilter);
      }

      const isCurrent = this.activeTab === 'CURRENT';
      return allDocs.filter((cert) => cert.current === isCurrent);
    },
  },
  methods: {
    async downloadCertificate(cert) {
      try {
        await this.$store.dispatch('download/getDocument', cert);
      } catch (err) {
        this.ui_showModal(true);
      }
    },
    formatFromNow(dateString) {
      const now = new Date();
      const date = new Date(dateString);
      const diffInSeconds = Math.floor((date - now) / 1000);
      const sign = diffInSeconds < 0 ? -1 : 1;
      const units = {
        year: 31536000,
        month: 2592000,
        day: 86400,
        hour: 3600,
        minute: 60,
        second: 1
      };

      for (const [unit, secondsInUnit] of Object.entries(units)) {
        const interval = Math.floor(Math.abs(diffInSeconds) / secondsInUnit);
        if (Math.abs(interval) >= 1) {
          return rtf.format(interval * sign, unit);
        }
      }
      return rtf.format(0, 'second');
    },
    getActivityForCert(cert) {
      if (cert.type === 'CERTIFICATE_ACTIVITY') {
        const position = this.activityCertificates.indexOf(cert);
        if (position === 0) {
          return get(this.subscription, 'risk.mainActivity.name', null);
        }
        if (position >= 1) {
          const activities = get(this.subscription, 'risk.activities', []);
          const secondary = activities[position - 1];
          return secondary?.name ?? null;
        }
        return null;
      }
      return this.mainActivity;
    },
    getCertificateName(cert) {
      if (cert.displayName) return cert.displayName;
      const certificate = this.certificates.find((c) => c.key === cert.type);
      if (certificate === undefined) return 'Attestation';
      return certificate.value;
    },
    openEmployeeTravelModal() {
      this.$store.dispatch('employee/clear');
      this.$store.dispatch('employee/setValue', { subscriptionId: this.subscription.id }, { root: true });
      this.ui_showModal(true);
      return Promise.resolve();
    },
    formatDate(tDate) {
      try {
        const date = new Date(tDate);
        let day = date.getDate();
        let month = date.getMonth() + 1;
        const year = date.getFullYear();

        if (day < 10) day = `0${day}`;
        if (month < 10) month = `0${month}`;
        return [day, month, year].join('/');
      } catch {
        return '';
      }
    },
  },
};
</script>
<style scoped>
@media screen and (max-width:767px) {
  .btn-grow {
    width: 100%;
  }
  .btn-download-pill {
    width: 100%;
  }
}
.btn-download-pill {
  border-radius: 50px;
  padding: 12px 36px;
  font-size: 18px;
  font-weight: 600;
  min-width: 180px;
}
.since {
  font-size: 14px;
  margin-top: 12px;
}
</style>
